import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CandidatesHeader } from "./components/CandidatesHeader";
import { BulkActionsBar } from "./components/BulkActionsBar";
import { ExportCandidatesModal } from "./components/ExportCandidatesModal";
import { Search, ExternalLink, Edit, Loader2, Trash2, Copy, BadgeCheck, Download } from "lucide-react";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  CommonTable,
  type Column,
  type TableAction,
} from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import {
  SelectDropdown,
  MultiSelectDropdown,
  CheckboxField,
  TextInput,
  type OptionItem,
} from "@/components/formfields";
import {
  useCandidateList,
  useUpdateCandidateStatus,
  useDeleteCandidate,
  useDuplicateCandidate,
} from "./hooks/useCandidate";
import { useInfiniteTeamList } from "@/pages/master-management/hooks/useTeam";
import { useInfiniteSkillList } from "@/pages/master-management/hooks/useSkill";
import type { Candidate } from "@/interface/candidate";
import type { TeamDetail, SkillDetail } from "@/interface/masterManagement";
import { cn } from "@/lib/utils";
import { toast } from "@/utils/toast.service";
import { useTableParams } from "@/utils/useTableParams";
import { getInitials } from "@/utils";
import { Avatar } from "@/components/ui/avatar";

const PAGE_SIZE = 10;

const STATUS_OPTIONS: OptionItem[] = [
  { label: "All Status", value: "all" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function Candidates() {
  const navigate = useNavigate();

  const {
    searchParams,
    search,
    page,
    updateParams,
    getParam,
    getArrayParam,
  } = useTableParams({ defaultLimit: PAGE_SIZE });

  // Read filter values from URL params
  const statusFilter = getParam("status", "active");
  const teamFilter = getParam("team", "");
  const teamNameFilter = getParam("team_name", "");
  const selectedSkills = useMemo(
    () => getArrayParam("skill"),
    [searchParams, getArrayParam],
  );

  const [searchQuery, setSearchQuery] = useState<string>(search);
  const [skillSearch, setSkillSearch] = useState<string>("");
  const [debouncedSkillSearch, setDebouncedSkillSearch] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [updatingId, setUpdatingId] = useState<number | string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [candidateToDelete, setCandidateToDelete] = useState<Candidate | null>(null);
  const [candidateToDuplicate, setCandidateToDuplicate] = useState<Candidate | null>(null);
  const [candidateToExport, setCandidateToExport] = useState<Candidate | null>(null);

  const updateStatusMutation = useUpdateCandidateStatus();
  const deleteCandidate = useDeleteCandidate();
  const duplicateCandidate = useDuplicateCandidate();

  const handleToggleStatus = (candidate: Candidate) => {
    const currentStatus = candidate.employment_status || "inactive";
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    setUpdatingId(candidate.id);
    updateStatusMutation.mutate(
      { id: candidate.id, employment_status: newStatus },
      {
        onSuccess: () => {
          // toast.success(
          //   `Status updated to ${newStatus === "active" ? "Active" : "Inactive"}`,
          // );
        },
        onError: (error: Error) => {
          // toast.error(error?.message || "Failed to update candidate status");
        },
        onSettled: () => {
          setUpdatingId(null);
        },
      },
    );
  };

  // Sync search input state when URL search parameter changes
  useEffect(() => {
    setSearchQuery(search);
  }, [search]);

  // Debounce main search query changes and update URL parameters
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== search) {
        updateParams({ search: searchQuery, page: 1 });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, search, updateParams]);

  // Debounce skill dropdown search query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSkillSearch(skillSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [skillSearch]);

  // Fetch teams with infinite scroll for dropdown filter
  const {
    data: teamData,
    fetchNextPage: fetchNextTeamPage,
    hasNextPage: hasNextTeamPage,
    isFetchingNextPage: isFetchingNextTeamPage,
    isLoading: isTeamsLoading,
  } = useInfiniteTeamList({ page: 1, page_size: 10 });

  const teamOptions: OptionItem[] = useMemo(() => {
    const defaultOpt: OptionItem = { label: "All Teams", value: "" };

    const mapped = teamData?.pages
      ? teamData.pages.flatMap((page) => {
        const items = Array.isArray(page?.data)
          ? page.data
          : page?.data?.results || [];
        return items
          .map((t: TeamDetail) => ({
            label: t.team_name || "",
            value: String(t.id),
          }))
          .filter((opt: OptionItem) => Boolean(opt.label));
      })
      : [];

    // If team is specified in the URL but not found in the current paginated list,
    // inject it using team_name from the URL
    if (teamFilter && !mapped.some((opt) => opt.value === String(teamFilter))) {
      const fallbackLabel = teamNameFilter || `Team #${teamFilter}`;
      mapped.unshift({ label: fallbackLabel, value: String(teamFilter) });
    }

    return [defaultOpt, ...mapped];
  }, [teamData, teamFilter, teamNameFilter]);

  // Fetch skills with infinite scroll and remote search for multiselect dropdown filter
  const {
    data: skillData,
    fetchNextPage: fetchNextSkillPage,
    hasNextPage: hasNextSkillPage,
    isFetchingNextPage: isFetchingNextSkillPage,
    isLoading: isSkillsLoading,
  } = useInfiniteSkillList({
    page: 1,
    page_size: 14,
    search: debouncedSkillSearch || undefined,
  });

  const skillOptions: OptionItem[] = useMemo(() => {
    if (!skillData?.pages) return [];

    return skillData.pages.flatMap((page) => {
      const items = Array.isArray(page?.data)
        ? page.data
        : page?.data?.results || [];
      return items
        .map((s: SkillDetail) => ({
          label: s.skill_name || "",
          value: String(s.id),
        }))
        .filter((opt: OptionItem) => Boolean(opt.label));
    });
  }, [skillData]);

  const {
    data: apiResponse,
    isLoading,
    isFetching,
  } = useCandidateList({
    page,
    page_size: PAGE_SIZE,
    search: search || undefined,
    employment_status: statusFilter === "all" ? undefined : statusFilter || undefined,
    team: teamFilter || undefined,
    skill: selectedSkills.length > 0 ? selectedSkills : undefined,
  });

  const responseData = apiResponse?.data;
  const candidates: Candidate[] = useMemo(
    () =>
      Array.isArray(responseData?.results)
        ? responseData.results
        : Array.isArray(responseData)
          ? responseData
          : [],
    [responseData],
  );

  const totalItems = responseData?.totalItems ?? candidates.length;
  const totalPages =
    responseData?.totalPages || Math.ceil(totalItems / PAGE_SIZE) || 1;

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === candidates.length && candidates.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(candidates.map((c) => c.id));
    }
  };

  const columns: Column<Candidate>[] = useMemo(
    () => [
      {
        key: "select",
        header: (
          <CheckboxField
            checked={
              candidates.length > 0 && selectedIds.length === candidates.length
            }
            onChange={toggleSelectAll}
            wrapperClassName="w-auto flex items-center"
          />
        ),
        render: (candidate) => (
          <CheckboxField
            checked={selectedIds.includes(candidate.id)}
            onChange={() => toggleSelect(candidate.id)}
            wrapperClassName="w-auto flex items-center"
          />
        ),
        width: "40px",
        className: "w-10",
      },
      {
        key: "username",
        header: "Candidate",
        render: (candidate) => {
          const name = candidate.full_name || candidate.username;
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={candidate.profile_picture}
                name={name}
                className="w-10 h-10 rounded-full border border-slate-200"
                fallbackClassName="text-sm"
              />
              <div>
                <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1">
                  <span
                    onClick={() => navigate(`/candidates/${candidate.id}`)}
                    className="cursor-pointer hover:text-purple-600 hover:underline transition-colors"
                  >
                    {name}
                  </span>
                  {candidate.is_original && (
                    <BadgeCheck
                      className="w-4 h-4 text-blue-500 shrink-0"
                      title="Original Profile"
                    />
                  )}
                </p>
                <p className="text-xs text-slate-400">ID: #{candidate.id}</p>
              </div>
            </div>
          );
        },
      },
      {
        key: "job_role",
        header: "Job Role",
        render: (candidate) => (
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {candidate.job_role || "N/A"}
          </span>
        ),
      },
      {
        key: "team",
        header: "Team",
        render: (candidate) => (
          <span className="text-xs font-medium text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-2.5 py-1 rounded-md">
            {candidate.team || "Unassigned"}
          </span>
        ),
      },
      {
        key: "skills",
        header: "Skills",
        render: (candidate) => {
          const skills = Array.isArray(candidate.skill) ? candidate.skill : [];
          if (skills.length === 0) {
            return (
              <span className="text-xs text-slate-400 font-medium">—</span>
            );
          }
          return (
            <div className="flex flex-wrap gap-1 max-w-xs">
              {skills.map((skill, idx) => (
                <span
                  key={`${skill}-${idx}`}
                  className="text-xs font-medium px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200/40 dark:border-purple-800/40"
                >
                  {skill}
                </span>
              ))}
            </div>
          );
        },
      },

      {
        key: "employment_status",
        header: "Status",
        render: (candidate) => {
          const currentStatus = (
            candidate.employment_status || "inactive"
          ).toLowerCase();
          const isActive = currentStatus === "active";
          const isUpdating = updatingId === candidate.id;

          return (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-2 w-2 rounded-full transition-colors duration-200",
                    isActive ? "bg-emerald-500" : "bg-red-500",
                  )}
                />
                <span
                  className={cn(
                    "text-xs font-medium px-2 py-1 rounded-full capitalize transition-colors duration-200",
                    isActive
                      ? "text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-500/10"
                      : "text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-500/10",
                  )}
                >
                  {candidate.employment_status || "inactive"}
                </span>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                disabled={isUpdating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleStatus(candidate);
                }}
                title={`Click to set status to ${isActive ? "inactive" : "active"}`}
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                  isActive
                    ? "bg-emerald-500 dark:bg-emerald-600"
                    : "bg-slate-300 dark:bg-slate-700",
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out flex items-center justify-center",
                    isActive ? "translate-x-4" : "translate-x-0",
                  )}
                >
                  {isUpdating && (
                    <Loader2 className="w-2.5 h-2.5 animate-spin text-slate-600" />
                  )}
                </span>
              </button>
            </div>
          );
        },
      },
    ],
    [candidates, selectedIds, updatingId, navigate],
  );

  const actions: TableAction<Candidate>[] = useMemo(
    () => [
      {
        label: "View Details",
        icon: () => <ExternalLink className="w-4 h-4" />,
        onClick: (row) => {
          navigate(`/candidates/${row.id}`);
        },
      },
      {
        label: "Edit Profile",
        icon: () => <Edit className="w-4 h-4" />,
        onClick: (row) => {
          navigate(`/candidates/${row.id}/edit`);
        },
      },
      {
        label: "Duplicate Profile",
        icon: () => <Copy className="w-4 h-4" />,
        visible: (row) => row.is_original,
        onClick: (row) => {
          setCandidateToDuplicate(row);
        },
      },
      {
        label: "Export",
        icon: () => <Download className="w-4 h-4" />,
        onClick: (row) => {
          setCandidateToExport(row);
        },
      },
      {
        label: "Delete",
        icon: () => <Trash2 className="w-4 h-4 text-red-500" />,
        onClick: (row) => {
          setCandidateToDelete(row);
        },
      },
    ],
    [navigate, deleteCandidate, duplicateCandidate],
  );

  return (
    <div className="max-w-[1920px] mx-auto space-y-8 min-h-screen">
      <CandidatesHeader />

      <div className="flex flex-col gap-6 bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm p-4 sm:p-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3 items-center flex-1 w-full">
            {/* Search Bar */}
            <div className="flex-1 max-w-md w-full">
              <TextInput
                placeholder="Search by name or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                prefix={<Search className="h-4 w-4 text-slate-400" />}
              />
            </div>

            {/* Status SelectDropdown Filter */}
            <div className="w-full sm:w-44">
              <SelectDropdown
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(val) => {
                  updateParams({ status: String(val), page: 1 });
                }}
                placeholder="All Status"
              />
            </div>

            {/* Team SelectDropdown Filter */}
            <div className="w-full sm:w-52">
              <SelectDropdown
                options={teamOptions}
                value={teamFilter}
                onChange={(val) => {
                  const selectedOpt = teamOptions.find(
                    (opt) => opt.value === String(val),
                  );
                  updateParams({
                    team: String(val),
                    team_name:
                      selectedOpt && selectedOpt.value
                        ? selectedOpt.label
                        : undefined,
                    page: 1,
                  });
                }}
                placeholder={isTeamsLoading ? "Loading teams..." : "All Teams"}
                isSearchable
                onMenuScrollToBottom={() => {
                  if (hasNextTeamPage && !isFetchingNextTeamPage) {
                    fetchNextTeamPage();
                  }
                }}
                hasMore={hasNextTeamPage}
                isLoadingMore={isFetchingNextTeamPage}
              />
            </div>

            {/* Skills MultiSelectDropdown Filter */}
            <div className="w-full sm:w-56">
              <MultiSelectDropdown
                options={skillOptions}
                value={selectedSkills}
                onChange={(vals) => {
                  updateParams({ skill: vals, page: 1 });
                }}
                onSearch={(query) => setSkillSearch(query)}
                placeholder={
                  isSkillsLoading ? "Loading skills..." : "All Skills"
                }
                isSearchable
                clearable
                onMenuScrollToBottom={() => {
                  if (hasNextSkillPage && !isFetchingNextSkillPage) {
                    fetchNextSkillPage();
                  }
                }}
                hasMore={hasNextSkillPage}
                isLoadingMore={isFetchingNextSkillPage}
              />
            </div>

            {isFetching && !isLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-purple-600 shrink-0" />
            )}
          </div>
        </div>

        <CommonTable
          columns={columns}
          data={candidates}
          actions={actions}
          loading={isLoading}
          emptyMessage="No candidates found matching your criteria."
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            limit={PAGE_SIZE}
            onPageChange={(newPage) => updateParams({ page: newPage })}
            entryLabel="candidates"
          />
        )}
      </div>

      {selectedIds.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.length}
          onClear={() => setSelectedIds([])}
          onExport={() => setIsExportModalOpen(true)}
        />
      )}

      <ExportCandidatesModal
        isOpen={isExportModalOpen || candidateToExport !== null}
        onClose={() => {
          setIsExportModalOpen(false);
          setCandidateToExport(null);
        }}
        selectedIds={candidateToExport ? [candidateToExport.id] : selectedIds}
        filename={(() => {
          const target =
            candidateToExport ||
            (selectedIds.length === 1
              ? candidates.find((c) => String(c.id) === String(selectedIds[0]))
              : null);
          if (!target) return "";
          const name = target.full_name || target.username || target.job_role || "";
          return name.trim().split(/\s+/).join("_");
        })()}
      />

      <ConfirmationDialog
        isOpen={!!candidateToDelete}
        onClose={() => setCandidateToDelete(null)}
        title="Delete Candidate"
        description={`Are you sure you want to delete ${candidateToDelete?.full_name ?? "this candidate"}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isPending={deleteCandidate.isPending}
        onConfirm={() => {
          if (!candidateToDelete) return;
          deleteCandidate.mutate(candidateToDelete.id);
        }}
      />

      <ConfirmationDialog
        isOpen={!!candidateToDuplicate}
        onClose={() => setCandidateToDuplicate(null)}
        title="Duplicate Profile"
        description={`Are you sure you want to duplicate ${candidateToDuplicate?.full_name ?? "this candidate"}'s profile?`}
        confirmText="Duplicate"
        cancelText="Cancel"
        confirmVariant="primary"
        isPending={duplicateCandidate.isPending}
        onConfirm={() => {
          if (!candidateToDuplicate) return;
          duplicateCandidate.mutate(candidateToDuplicate.id, {
            onSuccess: () => {
              // toast.success("Profile duplicated successfully");
              setCandidateToDuplicate(null);
            },
            onError: (error: Error) => {
              // toast.error(error?.message || "Failed to duplicate profile");
              setCandidateToDuplicate(null);
            },
          });
        }}
      />
    </div>
  );
}
